import { Button, App } from 'antd';
import { PDFDocument } from 'pdf-lib';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DownloadIcon } from './DownloadIcon';
import { Wrapper } from './DownloadPdf.styles';

import { useProfile } from '@/store';

type Props = {
  fileUrl: string;
  fileName?: string;
};

const logoUrl = 'https://assets.ucademy.com/logo.png';

const isReactNativeWebView = (): boolean =>
  typeof window !== 'undefined' && window.isReactNativeWebView === true;

export const DownloadPdf = ({ fileUrl, fileName }: Props) => {
  const { message } = App.useApp();
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { t } = useTranslation('StudentCourseTranslations');
  const { profile, roleCheck } = useProfile();

  const { isStudent } = roleCheck;

  useEffect(() => {
    const loadPdf = async () => {
      try {
        const response = await fetch(fileUrl);
        const pdfArrayBuffer = await response.arrayBuffer();
        setPdfBytes(pdfArrayBuffer);
      } catch (error) {
        console.error('Error al cargar el PDF:', error);
      }
    };

    loadPdf();
  }, [fileUrl]);

  const createWatermarkImage = async (): Promise<string> => {
    if (!profile) throw new Error('Profile no disponible');

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) throw new Error('No se pudo crear el contexto de canvas');

    canvas.width = 1000;
    canvas.height = 1000;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-Math.PI / 4);

    let yOffset = 0;

    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = logoUrl;
      });

      const maxWidth = 200;
      const ratio = img.height / img.width;
      const width = maxWidth;
      const height = width * ratio;

      ctx.drawImage(img, -width / 2, -height - 40, width, height);
      yOffset = height / 2;
    } catch (error) {
      console.error('Error al cargar la imagen:', error);
    }

    ctx.font = 'bold 24px Arial';
    ctx.fillStyle = 'rgba(80, 80, 80, 0.8)';

    const mainText = `${new Date().toLocaleDateString()}`;
    const mainTextMetrics = ctx.measureText(mainText);
    ctx.fillText(mainText, -mainTextMetrics.width / 2, yOffset + 0);

    const secondLineText = t('PDF_WATERMARK_1', {
      name: profile?.name || '',
      lastName: profile?.lastName || ''
    });
    const secondLineMetrics = ctx.measureText(secondLineText);
    ctx.fillText(secondLineText, -secondLineMetrics.width / 2, yOffset + 40);

    const thirdLineText = t('PDF_WATERMARK_2');
    const thirdLineMetrics = ctx.measureText(thirdLineText);
    ctx.fillText(thirdLineText, -thirdLineMetrics.width / 2, yOffset + 80);

    ctx.restore();

    return canvas.toDataURL('image/png');
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;

    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
  };

  const handleMobileDownload = async (pdfData: Uint8Array, finalName: string) => {
    if (!window.ReactNativeWebView) {
      message.error(t('FILE_DOWNLOAD_ERROR'));

      return;
    }

    const base64Data = arrayBufferToBase64(pdfData.buffer as ArrayBuffer);

    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'DOWNLOAD_FILE',
        data: base64Data,
        fileName: finalName,
        mimeType: 'application/pdf'
      })
    );
  };

  const handleWebDownload = (pdfData: Uint8Array, finalName: string) => {
    const isSafari =
      navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    const mimeType = isSafari ? 'application/octet-stream' : 'application/pdf';
    const blob = new Blob([pdfData as BlobPart], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = finalName;
    link.click();

    URL.revokeObjectURL(url);

    message.success(t('FILE_DOWNLOAD_SUCCESS'));
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!pdfBytes || !profile) {
      message.error(t('FILE_DOWNLOAD_ERROR'));

      return;
    }

    setIsDownloading(true);

    try {
      const name = fileName || fileUrl.split('/').pop() || 'documento-ucademy.pdf';
      const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;

      const pdfDoc = await PDFDocument.load(pdfBytes);

      if (isStudent) {
        const watermarkImageData = await createWatermarkImage();
        const watermarkImage = await pdfDoc.embedPng(watermarkImageData);

        const pages = pdfDoc.getPages();

        for (const page of pages) {
          const { width, height } = page.getSize();

          const watermarkWidth = 1000;
          const watermarkHeight = 1000;
          const pageAspectRatio = width / height;
          const watermarkAspectRatio = watermarkWidth / watermarkHeight;

          let finalWidth, finalHeight;

          if (pageAspectRatio > watermarkAspectRatio) {
            finalHeight = height * 0.8;
            finalWidth = finalHeight * watermarkAspectRatio;
          } else {
            finalWidth = width * 0.8;
            finalHeight = finalWidth / watermarkAspectRatio;
          }

          const x = (width - finalWidth) / 2;
          const y = (height - finalHeight) / 2;

          page.drawImage(watermarkImage, {
            x: x,
            y: y,
            width: finalWidth,
            height: finalHeight,
            opacity: 0.3
          });
        }
      }

      const modifiedPdfBytes = await pdfDoc.save();

      if (isReactNativeWebView()) {
        await handleMobileDownload(modifiedPdfBytes, finalName);
      } else {
        handleWebDownload(modifiedPdfBytes, finalName);
      }
    } catch (error) {
      console.error('Error al procesar el PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Wrapper style={{ position: 'relative' }}>
      <Button
        size="small"
        type="text"
        loading={isDownloading}
        icon={<DownloadIcon />}
        onClick={handleDownload}>
        {isDownloading && t('DOWNLOADING')}
      </Button>
    </Wrapper>
  );
};
